import { sendNotificationToStoreStaff, sendNotificationToStoreOwner } from './notification.service';
import { supabase } from '@/integrations/supabase/client';
import type { Order, OrderStatus } from '@/types/orders';

// Notification types for orders
export enum OrderNotificationType {
  ORDER_PLACED = 'order_placed',
  ORDER_STATUS_UPDATED = 'order_status_updated',
  ORDER_READY = 'order_ready',
  ORDER_COMPLETED = 'order_completed',
  ORDER_CANCELLED = 'order_cancelled',
}

// Send notification when order is placed
export const notifyOrderPlaced = async (order: Order) => {
  try {
    console.log('🔄 Sending order placed notification for order:', order.id);
    
    // Prepare notification data
    const notificationData = {
      orderId: order.id,
      storeId: order.store_id,
      tableId: order.table_id,
      tableName: order.tableName,
      tableNumber: order.tableNumber,
      status: order.status,
      totalAmount: order.totalAmount,
      action: 'created',
      items: order.items?.map(item => ({
        name: item.menu_item_name,
        quantity: item.quantity
      })),
      specialInstructions: order.specialInstructions,
      paymentMethod: order.payment_method,
      tipAmount: order.tip_amount,
      tipPercentage: order.tip_percentage,
      notificationType: OrderNotificationType.ORDER_PLACED,
      navigationRoute: `/dashboard/orders/${order.id}`,
    };

    // Send to store staff
    await sendNotificationToStoreStaff(
      order.store_id,
      `New Order #${order.id.substring(0, 8)}`,
      `Order from Table ${order.tableNumber} - $${order.totalAmount.toFixed(2)}`,
      notificationData
    );

    // Send to store owner
    await sendNotificationToStoreOwner(
      order.store_id,
      `New Order Received`,
      `Order #${order.id.substring(0, 8)} from Table ${order.tableNumber}`,
      notificationData
    );

    console.log('✅ Order placed notification sent successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error sending order placed notification:', error);
    return { success: false, error: 'Failed to send order notification' };
  }
};

// Send notification when order status is updated
export const notifyOrderStatusUpdate = async (
  order: Order,
  previousStatus: OrderStatus
) => {
  try {
    console.log('🔄 Sending order status update notification:', {
      orderId: order.id,
      previousStatus,
      newStatus: order.status
    });

    // Determine notification type based on status change
    let notificationType = OrderNotificationType.ORDER_STATUS_UPDATED;
    let title = 'Order Status Updated';
    let body = `Order #${order.id.substring(0, 8)} status changed`;

    switch (order.status) {
      case 'ready':
        notificationType = OrderNotificationType.ORDER_READY;
        title = 'Order Ready! 🎉';
        body = `Your order from Table ${order.tableNumber} is ready for pickup`;
        break;
      case 'completed':
        notificationType = OrderNotificationType.ORDER_COMPLETED;
        title = 'Order Completed';
        body = `Order #${order.id.substring(0, 8)} has been completed`;
        break;
      case 'cancelled':
        notificationType = OrderNotificationType.ORDER_CANCELLED;
        title = 'Order Cancelled';
        body = `Order #${order.id.substring(0, 8)} has been cancelled`;
        break;
      case 'preparing':
        title = 'Order Being Prepared';
        body = `Order #${order.id.substring(0, 8)} is now being prepared`;
        break;
    }

    // Prepare notification data
    const notificationData = {
      orderId: order.id,
      storeId: order.store_id,
      tableId: order.table_id,
      tableName: order.tableName,
      tableNumber: order.tableNumber,
      status: order.status,
      totalAmount: order.totalAmount,
      action: 'status_updated',
      previousStatus,
      items: order.items?.map(item => ({
        name: item.menu_item_name,
        quantity: item.quantity
      })),
      specialInstructions: order.specialInstructions,
      paymentMethod: order.payment_method,
      tipAmount: order.tip_amount,
      tipPercentage: order.tip_percentage,
      notificationType,
      navigationRoute: `/dashboard/orders/${order.id}`,
    };

    // Send to store staff
    await sendNotificationToStoreStaff(
      order.store_id,
      title,
      body,
      notificationData
    );

    // Send to store owner
    await sendNotificationToStoreOwner(
      order.store_id,
      title,
      body,
      notificationData
    );

    console.log('✅ Order status update notification sent successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error sending order status update notification:', error);
    return { success: false, error: 'Failed to send status update notification' };
  }
};

// Send notification to customer (if they have FCM token)
export const notifyCustomer = async (
  order: Order,
  notificationType: OrderNotificationType,
  title: string,
  body: string
) => {
  try {
    // For now, we'll skip customer notifications since FCM tokens are stored in users table
    // and we don't have a direct way to get customer FCM tokens from orders
    console.log('Customer notification skipped - FCM tokens stored in users table');
    return { success: true, message: 'Customer notification skipped' };
  } catch (error) {
    console.error('❌ Error sending customer notification:', error);
    return { success: false, error: 'Failed to send customer notification' };
  }
};

// Comprehensive order notification handler
export const handleOrderNotification = async (
  order: Order,
  action: 'placed' | 'status_updated',
  previousStatus?: OrderStatus
) => {
  try {
    console.log('🔄 Handling order notification:', { orderId: order.id, action });

    if (action === 'placed') {
      // Notify staff and owner about new order
      await notifyOrderPlaced(order);
    } else if (action === 'status_updated' && previousStatus) {
      // Notify about status change
      await notifyOrderStatusUpdate(order, previousStatus);

      // Send customer notification for certain status changes
      if (order.status === 'ready') {
        await notifyCustomer(
          order,
          OrderNotificationType.ORDER_READY,
          'Your Order is Ready! 🎉',
          `Order from Table ${order.tableNumber} is ready for pickup`
        );
      } else if (order.status === 'completed') {
        await notifyCustomer(
          order,
          OrderNotificationType.ORDER_COMPLETED,
          'Order Completed',
          `Thank you for your order!`
        );
      } else if (order.status === 'cancelled') {
        await notifyCustomer(
          order,
          OrderNotificationType.ORDER_CANCELLED,
          'Order Cancelled',
          `Your order has been cancelled. Please contact staff if you have questions.`
        );
      }
    }

    console.log('✅ Order notification handled successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error handling order notification:', error);
    return { success: false, error: 'Failed to handle order notification' };
  }
}; 